import React, { useState } from 'react';
import { motion } from 'framer-motion';
import CTA from './CTA';

const studioItems = [
  // Equipment
  { id: 1, category: 'equipment', image: '/pin13.jpg', title: 'Professional Camera Setup' },
  { id: 2, category: 'equipment', image: '/pin14.jpg', title: 'Studio Lighting System' },
  { id: 4, category: 'equipment', image: '/pin15.jpg', title: 'Video Production Gear' },

  // Setups
  { id: 5, category: 'setups', image: '/pin1.jpg', title: 'Portrait Photography Setup' },
  { id: 6, category: 'setups', image: '/pin8.jpg', title: 'Product Photography Station' },
  { id: 7, category: 'setups', image: '/pin11.jpg', title: 'Video Interview Setup' },
  { id: 8, category: 'setups', image: '/pin12.jpg', title: 'Creative Backdrop Arrangement' },

  // Process
  { id: 9, category: 'process', image: '/pin9.jpg', title: 'Pre-Production Planning' },
  { id: 11, category: 'process', image: '/pin10.jpg', title: 'Post-Production Editing' },
];

const categories = [
  { id: 'all', label: 'All Studio', count: 12 },
  { id: 'equipment', label: 'Equipment', count: 4 },
  { id: 'setups', label: 'Setups', count: 4 },
  { id: 'process', label: 'Process', count: 4 },
];

export default function Studio() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredItems = activeCategory === 'all'
    ? studioItems
    : studioItems.filter(item => item.category === activeCategory);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden pt-20 sm:pt-0">
        <img
          src="/ambani/3.JPG"
          alt="Professional Studio Showcase"
          className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-[15s] ease-out"
        />
        <div className="absolute inset-0 bg-black/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-white/70 text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-6 sm:mb-8"
            >
              Professional Studio
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-tight mb-6 sm:mb-10">
              Creative Studio Showcase
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-sm sm:text-base md:text-lg text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-12"
            >
              Step inside our professional studio where creativity meets precision. Discover our state-of-the-art equipment, innovative setups, and the meticulous process behind every stunning capture.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex items-center justify-center"
            >
              <a href="#gallery" className="group relative px-8 sm:px-12 py-4 sm:py-6 border-2 border-white/80 text-white text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.4em] uppercase bg-transparent hover:bg-white hover:text-black transition-all duration-500 rounded-sm backdrop-blur-sm">
                <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-4 text-white group-hover:text-black transition-colors duration-300">
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                  <span className="block w-3 h-3 rounded-full border-2 border-white/70 group-hover:border-black/70 transition-colors" />
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                </span>
                VIEW GALLERY
                <div className="absolute inset-0 bg-white/5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute bottom-10 left-10 opacity-30">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        </div>
        <div className="absolute top-20 right-20 opacity-20">
          <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </section>

      {/* Studio Gallery Section */}
      <section id="gallery" className="py-20 bg-gradient-to-br from-[#395958] via-[#2a4342] to-[#1a2f2e]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-white/70 text-xs tracking-[0.4em] uppercase mb-4">
              Our Studio
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-4">
              Studio Gallery
            </h2>
          </motion.div>

          {/* Category Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-8 py-3 text-sm tracking-wider uppercase transition-all duration-300 rounded-sm ${activeCategory === category.id
                  ? 'bg-white text-[#395958] shadow-lg'
                  : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                  }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </motion.div>

          {/* Masonry Gallery */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </div>
  );
}